import type { AudienceData } from "../interfaces/KYDMarketing";

/** User-facing message for duplicate semantic custom audiences (client + API). */
export const DUPLICATE_CUSTOM_AUDIENCE_MESSAGE =
  "Duplicate custom audience";

/**
 * Stable semantic identity for a custom audience row (Meta id, page, CSV, interests bundle, etc.).
 * Mirrors guided-flow list selection dedupe in the customer dashboard.
 */
export function audienceTargetingKey(a: AudienceData): string {
  // Page/CSV/interests/KYD rows may carry client-generated `id` before Meta assigns `external_id`.
  if (a.audience_type === "csv" && "file_key" in a && a.file_key) {
    const fk = String(a.file_key).trim();
    if (fk) return `csv:${fk}`;
  }
  if (a.audience_type === "page_engagers" && "page_id" in a && a.page_id) {
    const pid = String(a.page_id).trim();
    if (pid) {
      const pt = "page_type" in a && a.page_type ? a.page_type : "";
      return `page_engagers:${pt}:${pid}`;
    }
  }
  if (a.audience_type === "page_lookalike" && "page_id" in a && a.page_id) {
    const pid = String(a.page_id).trim();
    if (pid) {
      const pt = "page_type" in a && a.page_type ? a.page_type : "";
      const lk = "lookalike" in a ? a.lookalike : undefined;
      const ratio = lk?.ratio != null ? String(lk.ratio) : "";
      return `page_lookalike:${pt}:${pid}:${ratio}`;
    }
  }
  if (a.audience_type === "interests") {
    const keys = (a.interests ?? [])
      .map((i) => i.key)
      .filter(Boolean)
      .sort()
      .join(",");
    if (keys) return `interests:keys:${keys}`;
    const id = (a.id ?? "").trim();
    if (id) return `interests:${id}`;
    const created =
      "created_at" in a && a.created_at ? String(a.created_at).trim() : "";
    if (created) return `interests:created:${created}`;
    const nm = (a.name ?? "").trim();
    if (nm) return `interests:name:${nm}`;
  }
  if (a.audience_type === "kyd") {
    const nm = (a.name ?? "").trim();
    const created =
      "created_at" in a && a.created_at ? String(a.created_at).trim() : "";
    if (nm && created) return `kyd:${nm}:${created}`;
    if (nm) return `kyd:${nm}`;
  }
  const v = a.external_id ?? a.id;
  if (v != null && String(v).trim() !== "") {
    return String(v).trim();
  }
  return "";
}
